import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { formatNumber } from '@ztechapps/common.utils'
import { GENERIC_ERROR_MESSAGE, NETWORK_ERROR_MESSAGE } from '../../config/forms'

type SchoolInvitePreview = {
  schoolName: string
  teacherName: string
  teacherBalance: number
}

type SchoolInviteJoinResult = {
  schoolName: string
  poolBalance: number
}

// Invite landing page reached from the emailed link
// (`${websiteUrl}/join-school?token=...`, sendSchoolInviteEmail.ts) — standalone
// page with no marketing header/footer, mirroring DeleteAccount's placement
// outside Layout. The token in the query string is the only credential accepted;
// no sign-in is offered or required here. Preview/accept both proxy
// through this website's own server — never a direct call to the API.
export const JoinSchool = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [loading, setLoading] = useState(true)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [invite, setInvite] = useState<SchoolInvitePreview | null>(null)

  const [acknowledged, setAcknowledged] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [joined, setJoined] = useState<SchoolInviteJoinResult | null>(null)

  useEffect(() => {
    let cancelled = false

    const loadPreview = async () => {
      try {
        const response = await fetch('/api/school-invite/preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
        const result = await response.json().catch(() => ({}))
        if (cancelled) return

        if (!response.ok || !result?.success) {
          setPreviewError(result?.error ?? GENERIC_ERROR_MESSAGE)
          return
        }

        setInvite({
          schoolName: result.schoolName,
          teacherName: result.teacherName,
          teacherBalance: result.teacherBalance,
        })
      } catch {
        if (!cancelled) setPreviewError(NETWORK_ERROR_MESSAGE)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadPreview()
    return () => {
      cancelled = true
    }
  }, [token])

  const handleConfirm = async () => {
    if (!acknowledged || confirming) return

    try {
      setConfirming(true)
      setConfirmError(null)
      const response = await fetch('/api/school-invite/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result?.success) {
        setConfirmError(result?.error ?? GENERIC_ERROR_MESSAGE)
        return
      }

      setJoined({ schoolName: result.schoolName, poolBalance: result.poolBalance })
    } catch {
      setConfirmError(NETWORK_ERROR_MESSAGE)
    } finally {
      setConfirming(false)
    }
  }

  if (loading) {
    return (
      <main className="wrap join-school">
        <h1>Checking your invite…</h1>
        <p className="eyebrow">QuickScore · School invite</p>
      </main>
    )
  }

  if (previewError) {
    return (
      <main className="wrap join-school">
        <h1>Invite not valid</h1>
        <p className="eyebrow">QuickScore · School invite</p>
        <div className="form-err show" role="alert">
          {previewError}
        </div>
        <p>
          <Link to="/">Go back to the QuickScore home page</Link>
        </p>
      </main>
    )
  }

  if (joined) {
    return (
      <main className="wrap join-school">
        <h1>You&apos;re in!</h1>
        <p className="eyebrow">QuickScore · School invite</p>
        <div className="form-ok show">
          {`Your personal credits have moved into ${joined.schoolName}'s shared pool. ✓`}
        </div>
        <p>
          {`${joined.schoolName}'s pool now has ${formatNumber(joined.poolBalance, '0,0')} credits — every teacher `}
          at the school scans from and can top up this one balance.
        </p>
        <p>
          <Link to="/">Go back to the QuickScore home page</Link>
        </p>
      </main>
    )
  }

  // Neither loading, previewError nor joined — the preview succeeded and `invite` is set.
  const { schoolName, teacherName, teacherBalance } = invite as SchoolInvitePreview

  return (
    <main className="wrap join-school">
      <h1>{`Join ${schoolName}`}</h1>
      <p className="eyebrow">QuickScore · School invite</p>

      <p>
        {`${teacherName}, you're invited to join `}
        <strong>{schoolName}</strong>
        {'. Your current personal balance is '}
        <strong>{`${formatNumber(teacherBalance, '0,0')} credits`}</strong>
        {'.'}
      </p>

      <div className="join-school-warning" role="alert">
        {`This transfer is one-way and cannot be reversed. Confirming moves all ${formatNumber(
          teacherBalance,
          '0,0'
        )} of your credits into ${schoolName}'s shared pool for good — your personal balance becomes 0.`}
      </div>

      {confirmError ? (
        <div className="form-err show" role="alert">
          {confirmError}
        </div>
      ) : null}

      <label className="join-school-ack">
        <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.currentTarget.checked)} />I
        understand this transfer is one-way and cannot be reversed.
      </label>

      <button className="btn btn-primary" type="button" onClick={handleConfirm} disabled={!acknowledged || confirming}>
        {confirming ? 'Joining…' : 'Confirm and join'}
      </button>
    </main>
  )
}

export default JoinSchool
